// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).

// Shared pieces of the summary rules editor: option lists, path labels and options, error lookups, the Members context,
// and the row frame every rule uses (blueprint-sets Req 6.1-6.3).
import { type ReactNode } from 'react';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import { type SelectProps } from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import {
  MISSING_FIELD_MESSAGE,
  type CheckOp,
  type MemberPath,
  type RollupAggregate,
  type SummaryPath,
  type ValidationErrors,
} from '@bda/shared';
import { fieldTypeLabel } from '../blueprints/FieldsEditor';
import type { SummaryRulesMember } from './SummaryRulesEditor';

/** The marker of a rule whose reference no longer resolves (Req 6.3). */
export const MISSING_REFERENCE_LABEL = 'References a missing field';
export const DEPENDS_ON_MISSING_LABEL = 'Uses a rule that references a missing field';
const NOT_IN_SET = 'This blueprint is not in the set.';

export const AGGREGATES: Array<{ value: RollupAggregate; label: string; description: string }> = [
  { value: 'sum', label: 'Sum', description: 'Add up number values' },
  { value: 'count', label: 'Count', description: 'Count values or documents' },
  { value: 'min', label: 'Minimum', description: 'Smallest number value' },
  { value: 'max', label: 'Maximum', description: 'Largest number value' },
];
export const OPERATORS: Array<{ value: CheckOp; label: string }> = [
  { value: 'eq', label: 'Equals' },
  { value: 'lte', label: 'Less than or equal to' },
  { value: 'gte', label: 'Greater than or equal to' },
];
export const TOLERANCE_KINDS: Array<{ value: 'absolute' | 'percent'; label: string }> = [
  { value: 'absolute', label: 'Amount' },
  { value: 'percent', label: 'Percent of right value' },
];
export const SEGMENTS_VALUE = '\u0000segments';
export const NO_GROUPING = '\u0000none';
export const BY_MEMBER = '\u0000member';

export const isNumeric = (p: SummaryPath) => p.type === 'number' || p.type === 'integer';

/** "vendor › name", "amount (column of lineItems)", or the field name. */
export function pathLabel(path: MemberPath): string {
  const t = path.indexOf('[].');
  if (t >= 0) return `${path.slice(t + 3)} (column of ${path.slice(0, t)})`;
  return path.split('.').join(' › ');
}

export const pathOption = (p: SummaryPath): SelectProps.Option => ({
  value: p.path,
  label: pathLabel(p.path),
  description: fieldTypeLabel({ type: p.type }),
});

/** Strips the optional `summaryRules.` prefix of the API's keys. */
export function normalizeApiErrors(errors: ValidationErrors | undefined): ValidationErrors {
  const out: ValidationErrors = {};
  for (const [k, v] of Object.entries(errors ?? {}))
    out[k.startsWith('summaryRules.') ? k.slice('summaryRules.'.length) : k] = v;
  return out;
}

/** The first `${prefix}${n}` not in `taken`. */
export function nextFree(prefix: string, taken: Iterable<string>, caseInsensitive = false): string {
  const seen = new Set([...taken].map((t) => (caseInsensitive ? t.trim().toLowerCase() : t)));
  for (let n = 1; ; n++) {
    const candidate = `${prefix}${n}`;
    if (!seen.has(caseInsensitive ? candidate.toLowerCase() : candidate)) return candidate;
  }
}

export const replaceAt = <T,>(list: T[], i: number, item: T): T[] => list.map((x, j) => (j === i ? item : x));
export const removeAt = <T,>(list: T[], i: number): T[] => list.filter((_, j) => j !== i);

/** A visually hidden row name before the visible label, so each control is announced with its row. */
export const rowLabel = (row: string, text: string): ReactNode => (
  <>
    <span className="sr-only">{row}</span> {text}
  </>
);

export const ErrorText = ({ children }: { children?: string }) =>
  children ? (
    <Box color="text-status-error" variant="small">
      {children}
    </Box>
  ) : null;

export interface Ctx {
  members: SummaryRulesMember[];
  paths: Map<string, SummaryPath[]>;
  errors: ValidationErrors;
  readOnly: boolean;
}

export const memberOptions = (ctx: Ctx): SelectProps.Option[] =>
  ctx.members.map((m) => ({ value: m.blueprintId, label: m.name }));
export const selectedMember = (ctx: Ctx, id: string): SelectProps.Option =>
  memberOptions(ctx).find((o) => o.value === id) ?? { value: id, label: 'Blueprint not in the set' };
/** The current path as an option, even when it no longer exists in the Member's schema (marked, not dropped). */
export function selectedPath(ctx: Ctx, memberId: string, path: MemberPath): SelectProps.Option | null {
  if (path === '') return null;
  const p = ctx.paths.get(memberId)?.find((x) => x.path === path);
  return p ? pathOption(p) : { value: path, label: pathLabel(path), description: 'Missing from this blueprint' };
}
/** Field error with an empty path read as "Choose a field." rather than a missing reference. */
export const sourceError = (message: string | undefined, path: MemberPath | null): string | undefined =>
  message === MISSING_FIELD_MESSAGE && path === '' ? 'Choose a field.' : message;
export const isMissingRef = (message: string | undefined, path: MemberPath | null): boolean =>
  (message === MISSING_FIELD_MESSAGE && path !== '') || message === NOT_IN_SET;

export function RuleRow({
  title,
  marker,
  onRemove,
  readOnly,
  children,
}: {
  title: string;
  marker?: string;
  onRemove: () => void;
  readOnly: boolean;
  children: ReactNode;
}) {
  return (
    <section aria-label={title}>
      <Box padding={{ vertical: 's' }}>
        <SpaceBetween size="s">
          <SpaceBetween direction="horizontal" size="s" alignItems="center">
            <Box variant="h4" padding="n">
              {title}
            </Box>
            {marker && <StatusIndicator type="error">{marker}</StatusIndicator>}
            {!readOnly && <Button variant="icon" iconName="remove" ariaLabel={`Remove ${title}`} onClick={onRemove} />}
          </SpaceBetween>
          {children}
        </SpaceBetween>
      </Box>
    </section>
  );
}
